/**
 * Access Control API Client
 *
 * Enterprise-grade API client with:
 * - Strict typing using DTOs
 * - Proper error handling
 * - Centralized endpoint configuration
 * - Adapter integration
 */

import {
  AccessRulesResponse,
  ExpiryConfigsResponse,
  ExpiringItemsResponse,
} from './accessControl.dto';
import {
  mapAccessRules,
  mapAccessRule,
  mapExpiryConfigs,
  mapExpiryConfig,
  mapExpiringItems,
  type AccessControlRule,
  type ExpiryConfig,
  type ExpiringItem,
} from './accessControl.adapter';

/**
 * API Client Configuration
 */
const API_BASE = '/api/admin/access-control';

/**
 * Get authentication headers
 */
function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No authentication token found');
  }
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

/**
 * Handle API response errors
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || `API Error: ${response.status}`);
  }
  return response.json();
}

/**
 * Access Control API Client
 */
export const accessControlApi = {
  /**
   * Get all access control rules
   */
  async getRules(page: number = 1, limit: number = 20, search?: string): Promise<{
    rules: AccessControlRule[];
    total: number;
    page: number;
    limit: number;
    pages: number;
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    if (search) {
      params.append('search', search);
    }
    
    const response = await fetch(`${API_BASE}/rules?${params}`, {
      headers: getAuthHeaders(),
    });
    
    const result: AccessRulesResponse = await handleResponse(response);
    
    // Backend returns data as array for list, single object for create/update
    const rules = Array.isArray(result.data) ? result.data : [result.data];
    
    return {
      rules: mapAccessRules(rules),
      total: result.pagination.total,
      page: result.pagination.page,
      limit: result.pagination.limit,
      pages: result.pagination.pages,
    };
  },

  /**
   * Create a new access control rule
   */
  async createRule(rule: Partial<AccessControlRule>): Promise<AccessControlRule> {
    const response = await fetch(`${API_BASE}/rules`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        name: rule.name,
        description: rule.description,
        ruleType: rule.ruleType,
        ruleValue: rule.ruleValue,
        priority: rule.priority,
        isActive: rule.isActive,
      }),
    });
    
    const result: AccessRulesResponse = await handleResponse(response);
    // Backend returns data as array for list, single object for create/update
    const createdRule = Array.isArray(result.data) ? result.data[0] : result.data;
    if (!createdRule) {
      throw new Error('Failed to create access rule');
    }
    return mapAccessRule(createdRule);
  },

  /**
   * Update an existing access control rule
   */
  async updateRule(id: number, rule: Partial<AccessControlRule>): Promise<AccessControlRule> {
    const response = await fetch(`${API_BASE}/rules/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        name: rule.name,
        description: rule.description,
        ruleType: rule.ruleType,
        ruleValue: rule.ruleValue,
        priority: rule.priority,
        isActive: rule.isActive,
      }),
    });
    
    const result: AccessRulesResponse = await handleResponse(response);
    // Backend returns data as array for list, single object for create/update
    const updatedRule = Array.isArray(result.data) ? result.data[0] : result.data;
    if (!updatedRule) {
      throw new Error('No rule data returned from server');
    }
    return mapAccessRule(updatedRule);
  },

  /**
   * Delete an access control rule
   */
  async deleteRule(id: number): Promise<void> {
    const response = await fetch(`${API_BASE}/rules/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    
    await handleResponse(response);
  },

  /**
   * Get all expiry configurations
   */
  async getExpiryConfigs(page: number = 1, limit: number = 20): Promise<{
    configs: ExpiryConfig[];
    total: number;
    page: number;
    limit: number;
    pages: number;
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    const response = await fetch(`${API_BASE}/expiry?${params}`, {
      headers: getAuthHeaders(),
    });
    
    const result: ExpiryConfigsResponse = await handleResponse(response);
    
    // Backend returns data as array for list, single object for create/update
    const configs = Array.isArray(result.data) ? result.data : [result.data];
    
    return {
      configs: mapExpiryConfigs(configs),
      total: result.pagination.total,
      page: result.pagination.page,
      limit: result.pagination.limit,
      pages: result.pagination.pages,
    };
  },

  /**
   * Create a new expiry configuration
   */
  async createExpiryConfig(config: Partial<ExpiryConfig>): Promise<ExpiryConfig> {
    // Calculate expiryDate from expiryDays for backend
    const expiryDate = config.expiryDays
      ? new Date(Date.now() + config.expiryDays * 24 * 60 * 60 * 1000).toISOString()
      : new Date().toISOString();
    
    const response = await fetch(`${API_BASE}/expiry`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        name: config.name,
        description: config.description,
        expiryType: config.expiryType,
        expiryDate: expiryDate,
        warningDays: config.warningDays,
        companyId: config.companyId,
        isActive: config.isActive,
      }),
    });
    
    const result: ExpiryConfigsResponse = await handleResponse(response);
    // Backend returns data as array for list, single object for create/update
    const createdConfig = Array.isArray(result.data) ? result.data[0] : result.data;
    if (!createdConfig) {
      throw new Error('No config data returned from server');
    }
    return mapExpiryConfig(createdConfig);
  },

  /**
   * Update an existing expiry configuration
   */
  async updateExpiryConfig(id: number, config: Partial<ExpiryConfig>): Promise<ExpiryConfig> {
    // Calculate expiryDate from expiryDays for backend
    const expiryDate = config.expiryDays
      ? new Date(Date.now() + config.expiryDays * 24 * 60 * 60 * 1000).toISOString()
      : undefined;
    
    const response = await fetch(`${API_BASE}/expiry/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        name: config.name,
        description: config.description,
        expiryType: config.expiryType,
        expiryDate: expiryDate,
        warningDays: config.warningDays,
        companyId: config.companyId,
        isActive: config.isActive,
      }),
    });
    
    const result: ExpiryConfigsResponse = await handleResponse(response);
    // Backend returns data as array for list, single object for create/update
    const updatedConfig = Array.isArray(result.data) ? result.data[0] : result.data;
    if (!updatedConfig) {
      throw new Error('No config data returned from server');
    }
    return mapExpiryConfig(updatedConfig);
  },

  /**
   * Delete an expiry configuration
   */
  async deleteExpiryConfig(id: number): Promise<void> {
    const response = await fetch(`${API_BASE}/expiry/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    
    await handleResponse(response);
  },

  /**
   * Get expiring items (users and companies)
   */
  async getExpiringItems(days: number = 7): Promise<{
    users: ExpiringItem[];
    companies: ExpiringItem[];
  }> {
    const params = new URLSearchParams({
      days: days.toString(),
    });
    
    const response = await fetch(`${API_BASE}/expiring?${params}`, {
      headers: getAuthHeaders(),
    });
    
    const result: ExpiringItemsResponse = await handleResponse(response);
    
    const allItems = mapExpiringItems(result.data.users, result.data.companies);
    
    return {
      users: allItems.filter(item => item.type === 'user'),
      companies: allItems.filter(item => item.type === 'company'),
    };
  },
};
